/**
 * NavGlassOverlay.jsx
 * ═════════════════════════════════════════════════════════════════════
 * Full physically-based convex lens overlay for the navigation bar.
 *
 * Direct port of the LensClone implementation from marijanapav.com/shop:
 *   - Oblate spheroid ray tracing with front/back surface refraction
 *   - Per-channel IOR for chromatic aberration
 *   - Fresnel reflectance (Schlick), specular, rim glow, inner dim
 *   - HTML-in-Canvas: cloned DOM → texElementImage2D → GPU texture
 *
 * Fallback: renders nothing when the API is unavailable.
 *
 * @module NavGlassOverlay
 * ═════════════════════════════════════════════════════════════════════
 */

import * as React from "react"
import {
  isHtmlInCanvasSupported,
  isWebGLHtmlInCanvasSupported,
  createCloneSyncManager,
} from "../utils/html-in-canvas"
import {
  NAV_GLASS_VERTEX,
  NAV_GLASS_FRAGMENT,
  NAV_GLASS_DEFAULTS,
} from "../utils/glass-shaders"

// ─── WebGL Helpers ──────────────────────────────────────────────────────

function compileShader(gl, type, source) {
  const shader = gl.createShader(type)
  if (!shader) return null
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.warn("[NavGlass] shader compile error:", gl.getShaderInfoLog(shader))
    gl.deleteShader(shader)
    return null
  }
  return shader
}

function initGL(canvas) {
  const gl = canvas.getContext("webgl", {
    alpha: true,
    antialias: true,
    premultipliedAlpha: true,
  })
  if (!gl || typeof gl.texElementImage2D !== "function") return null

  const vs = compileShader(gl, gl.VERTEX_SHADER, NAV_GLASS_VERTEX)
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, NAV_GLASS_FRAGMENT)
  if (!vs || !fs) return null

  const program = gl.createProgram()
  if (!program) return null
  gl.attachShader(program, vs)
  gl.attachShader(program, fs)
  gl.linkProgram(program)
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.warn("[NavGlass] program link error:", gl.getProgramInfoLog(program))
    return null
  }
  gl.useProgram(program)

  // Full-screen quad (triangle strip)
  const buffer = gl.createBuffer()
  const posLoc = gl.getAttribLocation(program, "aPosition")
  if (!buffer) return null
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
    gl.STATIC_DRAW
  )
  gl.enableVertexAttribArray(posLoc)
  gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0)

  // Source texture
  const texture = gl.createTexture()
  if (!texture) return null
  gl.activeTexture(gl.TEXTURE0)
  gl.bindTexture(gl.TEXTURE_2D, texture)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)

  // Uniform locations — match the full physically-based shader
  const uniforms = {
    uSourceTex: gl.getUniformLocation(program, "uSourceTex"),
    uSourceSizePx: gl.getUniformLocation(program, "uSourceSizePx"),
    uNavCenterPx: gl.getUniformLocation(program, "uNavCenterPx"),
    uNavSizePx: gl.getUniformLocation(program, "uNavSizePx"),
    uLensMagnification: gl.getUniformLocation(program, "uLensMagnification"),
    uLensIOR: gl.getUniformLocation(program, "uLensIOR"),
    uAberrationAmount: gl.getUniformLocation(program, "uAberrationAmount"),
    uLensThickness: gl.getUniformLocation(program, "uLensThickness"),
    uBevelStartR: gl.getUniformLocation(program, "uBevelStartR"),
    uLensOblateness: gl.getUniformLocation(program, "uLensOblateness"),
    uRefractionDisplacementScale: gl.getUniformLocation(program, "uRefractionDisplacementScale"),
  }

  gl.uniform1i(uniforms.uSourceTex, 0)

  return { gl, texture, uniforms, program, vs, fs, buffer }
}

// ─── Component ──────────────────────────────────────────────────────────

const NavGlassOverlay = ({ cloneSelector = "[data-glass-root]", lightRef }) => {
  const canvasRef = React.useRef(null)
  const sourceRef = React.useRef(null)
  const glRef = React.useRef(null)
  const [supported, setSupported] = React.useState(false)
  const paramsRef = React.useRef(NAV_GLASS_DEFAULTS)

  // Feature detection (client-side only)
  React.useEffect(() => {
    setSupported(isHtmlInCanvasSupported() && isWebGLHtmlInCanvasSupported())
  }, [])

  // Init WebGL context and shaders (once, when supported)
  React.useEffect(() => {
    if (!supported) return
    const canvas = canvasRef.current
    if (!canvas) return
    try {
      canvas.setAttribute("layoutsubtree", "")
      glRef.current = initGL(canvas)
      if (!glRef.current) {
        canvas.removeAttribute("layoutsubtree")
        setSupported(false)
      }
    } catch (_) {
      canvas.removeAttribute("layoutsubtree")
      setSupported(false)
    }
  }, [supported])

  // Clone sync + render loop
  React.useEffect(() => {
    if (!supported) return
    const canvas = canvasRef.current
    const sourceHost = sourceRef.current
    const glState = glRef.current
    if (!canvas || !sourceHost || !glState) return

    try {
      const { gl, texture, uniforms } = glState
      let warmupFrame = null
      let syncManager = null

      const renderLens = () => {
        if (!syncManager) return
        const navEl = canvas.closest(".floating-nav")
        if (!navEl) return

        const dpr = Math.min(window.devicePixelRatio || 1, 3)
        const navRect = navEl.getBoundingClientRect()
        const drawW = Math.round(navRect.width * dpr)
        const drawH = Math.round(navRect.height * dpr)

        if (canvas.width !== drawW || canvas.height !== drawH) {
          canvas.width = drawW
          canvas.height = drawH
        }

        gl.viewport(0, 0, canvas.width, canvas.height)
        gl.clearColor(0, 0, 0, 0)
        gl.clear(gl.COLOR_BUFFER_BIT)
        gl.bindTexture(gl.TEXTURE_2D, texture)

        try {
          gl.texElementImage2D(
            gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, sourceHost
          )
        } catch (err) {
          if (err instanceof Error && err.message.includes("No cached paint record")) {
            if (warmupFrame !== null) cancelAnimationFrame(warmupFrame)
            warmupFrame = requestAnimationFrame(() => {
              warmupFrame = null
              requestRender()
            })
            return
          }
          throw err
        }

        // Source geometry
        const srcSize = syncManager.getSourceSize()
        const srcRoot = syncManager.getSourceRoot()
        const srcRect = srcRoot.getBoundingClientRect()

        // Nav center in page-source pixel coords
        const navCenterX = (navRect.left + navRect.width / 2) - srcRect.left
        const navCenterY = (navRect.top + navRect.height / 2) - srcRect.top

        const p = paramsRef.current

        // Set all uniforms
        gl.uniform2f(uniforms.uSourceSizePx, srcSize.width, srcSize.height)
        gl.uniform2f(uniforms.uNavCenterPx, navCenterX, navCenterY)
        gl.uniform2f(uniforms.uNavSizePx, navRect.width, navRect.height)
        gl.uniform1f(uniforms.uLensMagnification, p.magnification)
        gl.uniform1f(uniforms.uLensIOR, p.ior)
        gl.uniform1f(uniforms.uAberrationAmount, p.aberration)
        gl.uniform1f(uniforms.uLensThickness, p.thickness)
        gl.uniform1f(uniforms.uBevelStartR, p.bevelStart)
        gl.uniform1f(uniforms.uLensOblateness, p.oblateness)
        gl.uniform1f(uniforms.uRefractionDisplacementScale, p.refractionDisplacement)

        gl.enable(gl.BLEND)
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
      }

      const requestRender = () => {
        if (typeof canvas.requestPaint === "function") canvas.requestPaint()
      }

      canvas.onpaint = renderLens

      syncManager = createCloneSyncManager(
        canvas, sourceHost, cloneSelector, requestRender
      )

      if (!syncManager) {
        canvas.removeAttribute("layoutsubtree")
        setSupported(false)
        return
      }

      // CSS hook so parent nav can disable backdrop-filter
      const navEl = canvas.closest(".floating-nav")
      if (navEl) navEl.classList.add("has-glass-canvas")

      // Warmup
      warmupFrame = requestAnimationFrame(() => {
        warmupFrame = null
        requestRender()
      })

      // Continuous repaint keeps the HTML-in-canvas texture in sync with the page.
      let rafId = null
      function tick() {
        requestRender()
        rafId = requestAnimationFrame(tick)
      }
      rafId = requestAnimationFrame(tick)

      return () => {
        cancelAnimationFrame(rafId)
        if (warmupFrame !== null) cancelAnimationFrame(warmupFrame)
        canvas.onpaint = null
        syncManager.cleanup()
        if (navEl) navEl.classList.remove("has-glass-canvas")
      }
    } catch (_) {
      canvas.removeAttribute("layoutsubtree")
      setSupported(false)
    }
  }, [supported, cloneSelector, lightRef])

  if (!supported) return null

  return (
    <div className="nav-glass-overlay" aria-hidden="true">
      <canvas ref={canvasRef} className="nav-glass-canvas">
        <div ref={sourceRef} className="nav-glass-source" />
      </canvas>
    </div>
  )
}

export default NavGlassOverlay
